import React, { useState, useEffect } from 'react';
import { Home, Utensils, ShoppingBag, CheckCircle, AlertCircle, Users } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TeamIntentForm = ({ team }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [existingIntent, setExistingIntent] = useState(null);

  const [formData, setFormData] = useState({
    accommodation_required: false,
    accommodation_count: 0,
    food_required: false,
    food_count: 0,
    merchandise_required: false,
    merchandise_count: 0
  });

  useEffect(() => {
    fetchExistingIntent();
  }, []);

  const fetchExistingIntent = async () => {
    try {
      const response = await axios.get(`${API}/team/intent`);
      if (response.data.intent) {
        setExistingIntent(response.data.intent);
        setFormData({
          accommodation_required: response.data.intent.accommodation_required,
          accommodation_count: response.data.intent.accommodation_count,
          food_required: response.data.intent.food_required,
          food_count: response.data.intent.food_count,
          merchandise_required: response.data.intent.merchandise_required,
          merchandise_count: response.data.intent.merchandise_count
        });
      }
    } catch (err) {
      console.error('Error fetching intent:', err);
    }
  };

  const handleToggle = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: !prev[field],
      [`${field.replace('_required', '_count')}`]: !prev[field] ? 0 : prev[`${field.replace('_required', '_count')}`]
    }));
    setError('');
    setSuccess('');
  };

  const handleCountChange = (field, value) => {
    const count = parseInt(value) || 0;
    if (count > team.total_members) {
      setError(`Count cannot exceed team size (${team.total_members})`);
      return;
    }
    setFormData(prev => ({ ...prev, [field]: count }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    // Validation
    if (formData.accommodation_required && formData.accommodation_count === 0) {
      setError('Please enter accommodation count');
      setLoading(false);
      return;
    }
    if (formData.food_required && formData.food_count === 0) {
      setError('Please enter food count');
      setLoading(false);
      return;
    }
    if (formData.merchandise_required && formData.merchandise_count === 0) {
      setError('Please enter merchandise count');
      setLoading(false);
      return;
    }

    try {
      await axios.post(`${API}/team/intent`, formData);
      setSuccess(existingIntent ? 'Intent updated successfully!' : 'Intent submitted successfully!');
      fetchExistingIntent();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit intent');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl shadow-lg p-6 md:p-8 border-2 border-purple-200 mb-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-purple-600 p-3 rounded-full">
          <ShoppingBag className="text-white" size={24} />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Event Requirements</h2>
          <p className="text-sm text-gray-600">Help us plan better! Share your requirements below.</p>
          <p className="text-xs text-purple-600 font-medium mt-1">
            Early submissions may qualify for discounted early bird offers.
          </p>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-6 bg-red-50 border-2 border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="text-red-600 flex-shrink-0" size={20} />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-6 bg-green-50 border-2 border-green-200 rounded-lg p-4 flex items-start gap-3">
          <CheckCircle className="text-green-600 flex-shrink-0" size={20} />
          <p className="text-green-700">{success}</p>
        </div>
      )}

      {existingIntent && (
        <div className="mb-6 bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
          <p className="text-blue-800 font-semibold">
            ✓ You have already submitted your requirements. You can update them below if needed.
          </p>
          <p className="text-sm text-blue-600 mt-1">
            Last updated: {new Date(existingIntent.submitted_at).toLocaleString('en-IN')}
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Accommodation */}
        <div className="bg-white rounded-xl p-6 border-2 border-gray-200 hover:border-purple-300 transition-all">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <Home className="text-purple-600" size={32} />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-lg font-bold text-gray-800">Accommodation</h3>
                  <p className="text-sm text-gray-600">Do you require accommodation at IIT Gandhinagar?</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.accommodation_required}
                    onChange={() => handleToggle('accommodation_required')}
                    className="sr-only peer"
                  />
                  <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-purple-600"></div>
                </label>
              </div>
              {formData.accommodation_required && (
                <div className="mt-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <Users size={16} />
                    How many team members require accommodation?
                  </label>
                  <input
                    type="number"
                    min="1"
                    max={team.total_members}
                    value={formData.accommodation_count}
                    onChange={(e) => handleCountChange('accommodation_count', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all"
                    required={formData.accommodation_required}
                  />
                  <p className="text-xs text-gray-500 mt-1">Maximum: {team.total_members} (your team size)</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Food */}
        <div className="bg-white rounded-xl p-6 border-2 border-gray-200 hover:border-purple-300 transition-all">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <Utensils className="text-orange-600" size={32} />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-lg font-bold text-gray-800">Food</h3>
                  <p className="text-sm text-gray-600">Do you require food arrangements during the event?</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.food_required}
                    onChange={() => handleToggle('food_required')}
                    className="sr-only peer"
                  />
                  <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-orange-600"></div>
                </label>
              </div>
              {formData.food_required && (
                <div className="mt-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <Users size={16} />
                    How many team members require food?
                  </label>
                  <input
                    type="number"
                    min="1"
                    max={team.total_members}
                    value={formData.food_count}
                    onChange={(e) => handleCountChange('food_count', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-all"
                    required={formData.food_required}
                  />
                  <p className="text-xs text-gray-500 mt-1">Maximum: {team.total_members} (your team size)</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Merchandise */}
        <div className="bg-white rounded-xl p-6 border-2 border-gray-200 hover:border-purple-300 transition-all">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <ShoppingBag className="text-green-600" size={32} />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-lg font-bold text-gray-800">Merchandise</h3>
                  <p className="text-sm text-gray-600">Do you want official event merchandise (T-shirts, goodies)?</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.merchandise_required}
                    onChange={() => handleToggle('merchandise_required')}
                    className="sr-only peer"
                  />
                  <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-green-600"></div>
                </label>
              </div>
              {formData.merchandise_required && (
                <div className="mt-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <Users size={16} />
                    How many team members want merchandise?
                  </label>
                  <input
                    type="number"
                    min="1"
                    max={team.total_members}
                    value={formData.merchandise_count}
                    onChange={(e) => handleCountChange('merchandise_count', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all"
                    required={formData.merchandise_required}
                  />
                  <p className="text-xs text-gray-500 mt-1">Maximum: {team.total_members} (your team size)</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-bold py-4 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-105 shadow-lg flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <div className="spinner" style={{ width: '20px', height: '20px', borderWidth: '2px' }}></div>
              <span>{existingIntent ? 'Updating...' : 'Submitting...'}</span>
            </>
          ) : (
            <>
              <CheckCircle size={20} />
              <span>{existingIntent ? 'Update Requirements' : 'Submit Requirements'}</span>
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default TeamIntentForm;
